// src/components/dialog/DlgSettingsGlobal.tsx

import {
  ConstructionTwoTone,
  EmojiPeopleTwoTone,
  InfoTwoTone,
  MarkunreadMailboxTwoTone,
  NotificationsTwoTone,
  SaveTwoTone,
  UpdateTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { SalutationMode } from '@/types/karma/operator'

import { HStack, KTextField } from '..'
import {
  createStorageConfig,
  deleteStorageConfig,
  getStorage,
  notifySuccess,
  userLocales,
} from '../../api'
import {
  OperatorData,
  useAppConfig,
  useAppVersion,
  useMaintenanceMode,
  useMaintenanceText,
  useOperator,
  useSetMaintenanceMode,
  useSetMaintenanceText,
} from '../../hooks'

const onResetAppStorage = () => {
  deleteStorageConfig()
  createStorageConfig()

  caches
    .keys()
    .then(keyList => Promise.all(keyList.map(key => caches.delete(key))))

  notifySuccess('Lokale Einstellungen zurückgesetzt')

  location.reload()
}

type Props = {
  title?: string
}

export const DlgSettingsGlobal = ({ title = 'Einstellungen' }: Props) => {
  const theme = useTheme()
  const { white: color } = theme.palette

  const { appConfig, setAppConfig } = useAppConfig()
  const { data: appVersion } = useAppVersion() as any

  const opData: OperatorData = useOperator()

  const buildInfo = getStorage('buildInfo') || { environment: '', build: '' }

  const IS_DASHBOARD_EXPIRED = Date.parse(buildInfo.build) < appVersion

  const [message, setMessage] = useState('')
  const [messageActive, setMessageActive] = useState(0)
  const [missingSensorDataNotice, setMissingSensorDataNotice] = useState(0)

  const versionOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }

  const maintenanceText = useMaintenanceText()
  const isInMaintenance = useMaintenanceMode()
  const { setMaintenanceMode } = useSetMaintenanceMode()
  const { setMaintenanceText } = useSetMaintenanceText()

  useEffect(() => setMessageActive(isInMaintenance), [isInMaintenance])
  useEffect(() => setMessage(maintenanceText), [maintenanceText])

  useEffect(
    () =>
      setAppConfig(prev => ({
        ...prev,
        isMaintenance: Boolean(messageActive),
      })),
    [setAppConfig, messageActive]
  )

  useEffect(
    () => setAppConfig(prev => ({ ...prev, maintenanceMessage: message })),
    [setAppConfig, message]
  )

  const toggleDashboardAlerts = () =>
    setAppConfig(prev => ({ ...prev, alertsEnabled: !prev.alertsEnabled }))

  const onCloseSettings = () =>
    setAppConfig(prev => ({ ...prev, showSettings: !prev.showSettings }))

  const onSetTab = (event, setupTab) =>
    setAppConfig(prev => ({ ...prev, setupTab }))

  const onChangeSalutation = event => {
    const salutationMode: SalutationMode = event.target.value

    setAppConfig(prev => ({ ...prev, salutationMode }))
  }

  const onMarkUnread = () => {
    const alertsAcknowledged = []

    setAppConfig(prev => ({ ...prev, alertsAcknowledged }))
  }

  const onSetMaintenanceMessage = () => {
    const maintenanceMessage = message

    setAppConfig(prev => ({ ...prev, maintenanceMessage }))
    setMaintenanceText(maintenanceMessage)
  }

  const onToggleMaintenance = () => {
    const mode = messageActive === 0 ? 1 : 0

    setMessageActive(mode)
    setAppConfig(prev => ({ ...prev, isMaintenance: Boolean(mode) }))
    setMaintenanceMode(mode)
  }

  const onChangeAlertsMax = event => {
    const alertsMax = event.target.value

    setAppConfig(prev => ({ ...prev, alertsMax }))
  }

  const onToggleTimeMode = event => {
    const timeMode = event.target.value

    setAppConfig(prev => ({ ...prev, timeMode }))
  }

  const onToggleMissingSensorDataNotice = () => {
    const mode = missingSensorDataNotice === 0 ? 1 : 0
    setMissingSensorDataNotice(mode)
    setAppConfig(prev => ({
      ...prev,
      showMissingSensorDataNotice: Boolean(mode),
    }))
  }

  const onSetMessage = event => setMessage(event.target.value)

  return (
    <Dialog open={appConfig?.showSettings ?? false} onClose={onCloseSettings}>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Tabs
          centered
          indicatorColor={'primary'}
          onChange={onSetTab}
          textColor={'primary'}
          value={appConfig.setupTab}
        >
          <Tab icon={<EmojiPeopleTwoTone />} label={'Anrede'} value={0} />

          <Tab
            icon={<NotificationsTwoTone />}
            label={'Dashboard Warnungen'}
            value={1}
          />

          <Tab
            icon={
              <Badge
                color={'error'}
                badgeContent={IS_DASHBOARD_EXPIRED ? 1 : 0}
                invisible={!IS_DASHBOARD_EXPIRED}
              >
                <InfoTwoTone />
              </Badge>
            }
            label={'Version'}
            value={2}
          />

          {opData?.isAdmin && (
            <Tab icon={<ConstructionTwoTone />} label={'Wartung'} value={3} />
          )}
          <Tab icon={<InfoTwoTone />} label={'Hinweis'} value={4} />
        </Tabs>

        <Divider />

        {/* Anrede */}
        <Collapse in={appConfig.setupTab === 0} sx={{ mt: 3 }}>
          <RadioGroup
            onChange={onChangeSalutation}
            value={appConfig.salutationMode}
          >
            <FormControlLabel
              control={<Radio />}
              label={'keine Anrede'}
              value={'noSalutation'}
            />
            <FormControlLabel
              control={<Radio />}
              label={'Vor- und Nachname'}
              value={'default'}
            />
            <FormControlLabel
              control={<Radio />}
              label={'Vorname'}
              value={'short'}
            />
          </RadioGroup>
        </Collapse>

        {/* dashboard alerts */}
        <Collapse in={appConfig.setupTab === 1}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={appConfig.alertsEnabled}
                  onChange={toggleDashboardAlerts}
                />
              }
              label={'Dashboard Warnungen'}
              labelPlacement={'start'}
              sx={{ mb: 1 }}
            />
          </FormGroup>

          <Collapse in={appConfig.alertsEnabled}>
            <Alert severity={'info'} sx={{ mb: 2 }}>
              <AlertTitle>Warnungen konfigurieren</AlertTitle>
              Abhängig von Ihrem Anwendungsprofil können Sie Warnungen Ihrer
              entsprechenden Module anzeigen oder ausblenden.
            </Alert>

            <Divider />

            <FormControl fullWidth>
              <KTextField
                label={'maximal anzeigen'}
                onChange={onChangeAlertsMax}
                sx={{ mb: 2, mt: 2, ml: 0, mr: 0 }}
                type={'number'}
                unit={'Warnungen'}
                value={appConfig.alertsMax}
              />
            </FormControl>

            <Divider />

            <FormControl sx={{ mt: 2, mb: 2 }}>
              <FormLabel>Zeitangaben</FormLabel>
              <RadioGroup
                onChange={onToggleTimeMode}
                value={appConfig.timeMode}
              >
                <FormControlLabel
                  control={<Radio />}
                  label={'absolut'}
                  value={'absolute'}
                />
                <FormControlLabel
                  control={<Radio />}
                  label={'relativ'}
                  value={'relative'}
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            <Alert severity={'info'} sx={{ mt: 2 }}>
              <AlertTitle>Warnungen als ungelesen markieren</AlertTitle>
              Bestätigte Warnungen werden als ungelesen markiert und wieder
              angezeigt. (max. 100 Warnungen)
            </Alert>
          </Collapse>
        </Collapse>

        {/* version */}
        <Collapse in={appConfig.setupTab === 2}>
          <Alert severity={IS_DASHBOARD_EXPIRED ? 'warning' : 'success'}>
            <AlertTitle>
              {IS_DASHBOARD_EXPIRED
                ? 'Aktualisierung verfügbar'
                : 'Sie verwenden die aktuelle Version'}
            </AlertTitle>
            {IS_DASHBOARD_EXPIRED &&
              'Um die neue Version zu erhalten, klicken Sie auf Aktualisieren und starten Sie danach Ihren Browser neu.'}
            <br />

            <small>
              Ihre Version:{' '}
              {new Date(Date.parse(buildInfo.build)).toLocaleDateString(
                userLocales,
                versionOptions
              )}
            </small>
            <br />
            <small>
              verfügbare Version:{' '}
              {new Date(appVersion).toLocaleDateString(
                userLocales,
                versionOptions
              )}
            </small>
          </Alert>
        </Collapse>

        {/* wartung */}
        <Collapse in={appConfig.setupTab === 3}>
          <KTextField
            label={'Meldung'}
            onChange={onSetMessage}
            maxRows={30}
            multiline
            sx={{ whiteSpace: 'pre-line', wordWrap: 'break-word' }}
            value={message}
          />

          <Alert severity={'info'} sx={{ m: 1 }}>
            <AlertTitle>Wartungsmeldung</AlertTitle>
            Wenn aktiviert, wird eine Wartungsmeldung eingeblendet. Der Text ist
            frei wählbar.
          </Alert>
        </Collapse>
        {/* wartung */}
        <Collapse in={appConfig.setupTab === 4}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box>
              <Typography>Fehlende Messdaten</Typography>
            </Box>
            <Box>
              <Tooltip
                title={
                  'Anzeige von Hinweisen, um sofort erkennen zu können, wenn im gewählten Zeitraum keine Messdaten für einen Messpunkt vorliegen'
                }
              >
                <IconButton>
                  <InfoTwoTone />
                </IconButton>
              </Tooltip>
              <Switch
                checked={Boolean(appConfig.showMissingSensorDataNotice)}
                color={'secondary'}
                onChange={onToggleMissingSensorDataNotice}
              />
            </Box>
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions>
        {appConfig.setupTab === 1 && appConfig.alertsEnabled && (
          <Tooltip title={'Als ungelesen markieren'}>
            <IconButton onClick={onMarkUnread} sx={{ color }}>
              <MarkunreadMailboxTwoTone />
            </IconButton>
          </Tooltip>
        )}
        {appConfig.setupTab === 2 && (
          <Button color={'success'} onClick={onResetAppStorage}>
            <UpdateTwoTone sx={{ mr: 1 }} /> Aktualisieren
          </Button>
        )}
        {appConfig.setupTab === 3 && (
          <HStack
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{ ml: 3, width: '100%' }}
          >
            <Typography>
              Wartungsmeldung ist:{' '}
              {['deaktiviert', 'aktiviert'][Number(appConfig.isMaintenance)]}
            </Typography>

            <HStack>
              <Tooltip title={'Meldung speichern'}>
                <IconButton onClick={onSetMaintenanceMessage} sx={{ color }}>
                  <SaveTwoTone />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={`Wartungsmeldung ${
                  ['aktivieren', 'deaktivieren'][
                    Number(appConfig.isMaintenance)
                  ]
                }`}
              >
                <Switch
                  checked={Boolean(appConfig.isMaintenance)}
                  color={'secondary'}
                  onChange={onToggleMaintenance}
                />
              </Tooltip>
            </HStack>
          </HStack>
        )}
      </DialogActions>
    </Dialog>
  )
}
